import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, ResultTable } from '../_fitnessHelpers';

// Band-size lookup by underbust (inches) — matches the industry standard mapping.
// Each row: US, UK, EU (cm), FR (cm+15), IT (index), AU/NZ, JP (=EU), KR (=EU), BR (=cm)
const BAND_TABLE = [
  { in: 28, us: 28, uk: 28, eu: 60, fr: 75, it: 0, au: 6,  jp: 60, kr: 60, br: 60 },
  { in: 30, us: 30, uk: 30, eu: 65, fr: 80, it: 1, au: 8,  jp: 65, kr: 65, br: 65 },
  { in: 32, us: 32, uk: 32, eu: 70, fr: 85, it: 2, au: 10, jp: 70, kr: 70, br: 70 },
  { in: 34, us: 34, uk: 34, eu: 75, fr: 90, it: 3, au: 12, jp: 75, kr: 75, br: 75 },
  { in: 36, us: 36, uk: 36, eu: 80, fr: 95, it: 4, au: 14, jp: 80, kr: 80, br: 80 },
  { in: 38, us: 38, uk: 38, eu: 85, fr: 100, it: 5, au: 16, jp: 85, kr: 85, br: 85 },
  { in: 40, us: 40, uk: 40, eu: 90, fr: 105, it: 6, au: 18, jp: 90, kr: 90, br: 90 },
  { in: 42, us: 42, uk: 42, eu: 95, fr: 110, it: 7, au: 20, jp: 95, kr: 95, br: 95 },
  { in: 44, us: 44, uk: 44, eu: 100, fr: 115, it: 8, au: 22, jp: 100, kr: 100, br: 100 },
  { in: 46, us: 46, uk: 46, eu: 105, fr: 120, it: 9, au: 24, jp: 105, kr: 105, br: 105 },
];

// Cup lookup by (bust - band) difference in inches
const CUP_TABLE = [
  { diff: -0.5, us: '',    uk: '',    eu: '',   fr: '',   au: '',   jp: '',   br: '' },
  { diff: 0,    us: 'AA',  uk: 'AA',  eu: 'AA', fr: 'AA', au: 'AA', jp: 'AAA', br: 'AA' },
  { diff: 1,    us: 'A',   uk: 'A',   eu: 'A',  fr: 'A',  au: 'A',  jp: 'AA', br: 'A' },
  { diff: 2,    us: 'B',   uk: 'B',   eu: 'B',  fr: 'B',  au: 'B',  jp: 'A',  br: 'B' },
  { diff: 3,    us: 'C',   uk: 'C',   eu: 'C',  fr: 'C',  au: 'C',  jp: 'B',  br: 'C' },
  { diff: 4,    us: 'D',   uk: 'D',   eu: 'D',  fr: 'D',  au: 'D',  jp: 'C',  br: 'D' },
  { diff: 5,    us: 'DD',  uk: 'DD',  eu: 'E',  fr: 'E',  au: 'DD', jp: 'D',  br: 'E' },
  { diff: 6,    us: 'DDD', uk: 'E',   eu: 'F',  fr: 'F',  au: 'E',  jp: 'E',  br: 'F' },
  { diff: 7,    us: 'F',   uk: 'F',   eu: 'G',  fr: 'G',  au: 'F',  jp: 'F',  br: 'G' },
  { diff: 8,    us: 'G',   uk: 'FF',  eu: 'H',  fr: 'H',  au: 'FF', jp: 'G',  br: 'H' },
  { diff: 9,    us: 'H',   uk: 'G',   eu: 'I',  fr: 'I',  au: 'G',  jp: 'H',  br: 'I' },
  { diff: 10,   us: 'I',   uk: 'GG',  eu: 'J',  fr: 'J',  au: 'GG', jp: 'I',  br: 'J' },
];

function usBandFromUnderbust(inches) {
  const r = Math.round(inches);
  return r % 2 === 0 ? r : r + 1;
}
function findBandRow(usBand) {
  return BAND_TABLE.find(b => b.us === usBand) || BAND_TABLE[Math.max(0, Math.min(BAND_TABLE.length - 1, usBand < 28 ? 0 : usBand > 46 ? BAND_TABLE.length - 1 : 0))];
}
function findCupRow(diffIn) {
  const rounded = Math.round(diffIn);
  return CUP_TABLE.find(c => c.diff === rounded) || CUP_TABLE[CUP_TABLE.length - 1];
}

export default function BraSizeCalc() {
  const [tab, setTab] = useState(0);
  const [underbust, setUnderbust] = useState(30);
  const [bust, setBust] = useState(34);
  const clear = () => { setUnderbust(30); setBust(34); };

  const result = useMemo(() => {
    let u = +underbust || 0, b = +bust || 0;
    if (!u || !b || b < u) return null;
    if (tab === 1) { u = u / 2.54; b = b / 2.54; }
    const band = usBandFromUnderbust(u);
    const bandRow = findBandRow(band);
    const cupRow = findCupRow(b - band);
    return {
      band,
      cup: cupRow.us,
      diff: (b - band).toFixed(1),
      rows: [
        ['US / Canada', `${bandRow.us}${cupRow.us || ''}`],
        ['UK', `${bandRow.uk}${cupRow.uk || cupRow.us}`],
        ['Europe (EU, DE, SE, NL, BE, NO, CH)', `${bandRow.eu}${cupRow.eu || cupRow.us}`],
        ['France, Spain, Portugal', `${bandRow.fr}${cupRow.fr || cupRow.us}`],
        ['Italy (IT)', `${bandRow.it}${cupRow.us}`],
        ['Australia, New Zealand', `${bandRow.au}${cupRow.au || cupRow.us}`],
        ['Japan (JP)', `${bandRow.jp}${cupRow.jp || cupRow.us}`],
        ['South Korea (KR)', `${bandRow.kr}${cupRow.jp || cupRow.us}`],
        ['Brazil (BR)', `${bandRow.br}${cupRow.br || cupRow.us}`],
        ['India (IN, same as UK)', `${bandRow.uk}${cupRow.uk || cupRow.us}`],
      ],
    };
  }, [tab, underbust, bust]);

  return (
    <CalcShell look="cnet" title="Bra Size Calculator" subtitle="Find your band + cup size and convert across US/UK/EU/FR/IT/AU/JP/KR/BR/IN standards.">
      <CalcNetPanel slug="bra" tabs={['Inches (US)', 'Centimeters']} activeTab={tab} onTabChange={setTab} onClear={clear} right={
        result ? (
          <div>
            <div className="text-lg text-slate-800">Recommended size (US)</div>
            <div className="text-2xl font-bold text-[#2f9c3f]" data-testid="bra-result">{result.band}{result.cup}</div>
            <div className="text-xs text-slate-500 mt-1">Bust − Band = {result.diff}"</div>
            <div className="mt-4">
              <ResultTable testid="bra-conv-table" headers={['Country / Standard', 'Size']} rows={result.rows} />
            </div>
          </div>
        ) : <div className="text-slate-500 text-sm">Enter both measurements to see sizes.</div>
      }>
        <Row label="Underbust"><UnitInput value={underbust} onChange={setUnderbust} unit={tab === 0 ? 'in' : 'cm'} testid="bra-underbust"/></Row>
        <Row label="Bust"><UnitInput value={bust} onChange={setBust} unit={tab === 0 ? 'in' : 'cm'} testid="bra-bust"/></Row>
        <p className="text-xs text-slate-600 mt-2">Underbust: snug around the ribcage under bust. Bust: loose over fullest part of chest. For most accurate results, measure while wearing a non-padded bra.</p>
      </CalcNetPanel>
    </CalcShell>
  );
}
